import React from 'react';
import { DateTime, Interval } from 'luxon';
import { locales } from '@americanexpress/conditional-features';
import {
  DEFAULT_DATE_FORMAT,
  DATE_INPUT_FORMAT,
  WEEKDAY_FORMAT,
} from '../constants/dateFormatConstants';
import { DAY, DAYS, START_DATE } from '../constants/dateConstants';
import {
  getMaxShownDateString,
  getMinShownDateString,
  getNextAvailableStringDate,
  getPreviousAvailableStringDate,
} from '../selectors/dateSelectors';

export const generateDatesArrayForInterval = (startDate, endDate) => {
  if (!startDate || !endDate || endDate < startDate) return [];
  return [
    ...Interval.fromDateTimes(
      startDate,
      endDate
    ).splitBy({ days: 1 }).map((d) => d.start),
  ];
};

export const areLuxonDatesEqual = (dateTime1, dateTime2) => {
  if (dateTime1 === null || dateTime2 === null) {
    return dateTime1 === dateTime2;
  }

  return dateTime1.isValid
    && dateTime2.isValid
    && dateTime1.valueOf() === dateTime2.valueOf();
};

export const stringToLuxon = (stringDate) => (
  stringDate ? DateTime.fromFormat(stringDate, DEFAULT_DATE_FORMAT) : null
);

export const luxonToString = (luxonDate) => (
  luxonDate ? luxonDate.toFormat(DEFAULT_DATE_FORMAT) : null
);

export const formatHeading = (locale, luxonDate, formattingOptions) => {
  try {
    const formatter = new Intl.DateTimeFormat(locale, formattingOptions);
    return formatter.formatToParts(luxonDate)
      .map(({ type, value }) => {
        switch (type) {
          case 'dayPeriod':
            return <sup>{value}</sup>;
          default:
            return value;
        }
      });
  } catch (e) {
    // Fallback for Android devices where the Intl polyfill generates invalid regex
    // from CLDR locale data. Uses Luxon's formatting instead.
    return [luxonDate.toLocaleString(formattingOptions)];
  }
};

export const convertDefaultFormatStringDateToLocaleFormat = (stringDate) => {
  if (!stringDate) {
    return '';
  }
  const luxonDate = DateTime.fromFormat(stringDate, DEFAULT_DATE_FORMAT);
  const { EN_AU, EN_SG } = locales;
  if (luxonDate.locale === EN_AU || luxonDate.locale === EN_SG) {
    return luxonDate.toLocaleString(DATE_INPUT_FORMAT);
  }
  return luxonDate.toLocaleString();
};

export const formatAvailableDates = (datesAvailable) => {
  if (!Array.isArray(datesAvailable)) {
    return null;
  }
  return datesAvailable.reduce((accumulator, currentDate) => {
    accumulator[currentDate] = true;
    return accumulator;
  }, {});
};

export const luxonDateMinNights = (luxonDate, minNights, minNightsPerDate) => {
  if (luxonDate && typeof minNightsPerDate === 'object') {
    const stringDate = luxonDate.toFormat(DEFAULT_DATE_FORMAT);
    if (stringDate in minNightsPerDate) {
      return minNightsPerDate[stringDate];
    }
  }
  return minNights;
};

export const isWithinMinMaxRange = ({
  referenceDate,
  luxonDate,
  minNights,
  maxNights,
}) => {
  const daysDiff = luxonDate.diff(referenceDate, DAYS).days;
  return !(daysDiff >= 0 && (
    daysDiff < minNights || daysDiff > maxNights
  ));
};

export const isBeyondMaxDateFromToday = ({
  maxDaysAdvanceFromToday,
  luxonDate,
}) => DateTime.now().startOf(DAY).plus({ days: maxDaysAdvanceFromToday })
  < luxonDate.startOf(DAY);

export const isBeforeMinDate = ({
  luxonDate,
  minStartDate,
}) => {
  if (minStartDate && minStartDate.isValid) {
    return minStartDate.startOf(DAY) > luxonDate.startOf(DAY);
  }
  return DateTime.now().startOf(DAY) > luxonDate.startOf(DAY);
};

export const getNearestAvailableStringDate = (state, isForward, stringDate) => {
  let baseStringDate = stringDate;
  let nearestAvailableStringDate;
  if (!baseStringDate) {
    if (isForward) {
      baseStringDate = getMaxShownDateString({ state });
    } else {
      baseStringDate = getMinShownDateString({ state });
    }
  }
  if (isForward) {
    nearestAvailableStringDate = getNextAvailableStringDate({ state }, baseStringDate);
    if (!nearestAvailableStringDate) {
      nearestAvailableStringDate = getPreviousAvailableStringDate({ state }, baseStringDate);
    }
  } else {
    nearestAvailableStringDate = getPreviousAvailableStringDate({ state }, baseStringDate);
    if (!nearestAvailableStringDate) {
      nearestAvailableStringDate = getNextAvailableStringDate({ state }, baseStringDate);
    }
  }
  return nearestAvailableStringDate;
};

export const getAriaLabel = (stringDate, placeholder) => {
  const luxonDate = stringToLuxon(stringDate);
  const weekDay = luxonDate?.toFormat(WEEKDAY_FORMAT);
  return weekDay ? `${weekDay} ${placeholder}` : placeholder;
};

export const setCalendarCurrentDate = (focusedField, startDate, endDate) => {
  if (focusedField === START_DATE) {
    return startDate;
  }
  return endDate || startDate;
};
